import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AUSPICIOUS_ACTIVITIES, type AuspiciousActivity, type LunarDay, ZODIAC_SIGNS } from '@yadviga/shared'
import { api } from '../../api/client'
import { useSession } from '../../store/user.store'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { ErrorState } from '../../components/ui/ErrorState'
import { PageHeader } from '../../components/ui/PageHeader'
import { MoonPhaseIcon } from '../../components/luna/MoonPhaseIcon'

/**
 * "Good days for X" — chip filter at top picks an activity, list below
 * shows upcoming days where lunar day × moon sign × VOC line up.
 */
export function LunaAuspiciousRoute() {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)
  const lang = (user?.language ?? 'ru') as 'ru' | 'en'
  const [activity, setActivity] = useState<AuspiciousActivity>('starting')

  const query = useQuery({
    queryKey: ['lunar', 'auspicious', activity],
    queryFn: () => api.lunarAuspicious({ activity }),
    staleTime: 1000 * 60 * 60 * 6,
  })

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={t('lunar.auspicious_title')}
        subtitle={t('lunar.auspicious_desc')}
        backTo="/luna"
      />

      <div className="flex flex-wrap gap-1.5">
        {AUSPICIOUS_ACTIVITIES.map((a) => (
          <button
            key={a}
            onClick={() => setActivity(a)}
            className={
              'rounded-full px-3 py-1.5 text-sm transition active:scale-[0.97] ' +
              (a === activity
                ? 'bg-sage-800 text-cream'
                : 'bg-sage-100 text-sage-700 hover:bg-sage-200')
            }
          >
            {t(`lunar.activity.${a}` as never)}
          </button>
        ))}
      </div>

      {query.isLoading && <LoadingDots compact />}
      {query.isError && <ErrorState onRetry={() => query.refetch()} />}
      {query.data && query.data.length === 0 && (
        <p className="rounded-2xl bg-white/60 p-5 text-center text-sm text-sage-700 ring-1 ring-sage-200/60">
          {t('lunar.no_auspicious_in_month')}
        </p>
      )}
      {query.data && query.data.length > 0 && (
        <ul className="flex flex-col gap-2">
          {query.data.map((day) => (
            <DayRow key={day.date} day={day} lang={lang} />
          ))}
        </ul>
      )}
    </div>
  )
}

function DayRow({ day, lang }: { day: LunarDay; lang: 'ru' | 'en' }) {
  const { t } = useTranslation()
  const sign = ZODIAC_SIGNS.find((s) => s.id === day.moonSign)
  const signLabel = sign ? (lang === 'en' ? sign.nameEn : sign.nameRu) : day.moonSign
  return (
    <li className="flex items-center gap-3 rounded-2xl bg-white/70 p-3 ring-1 ring-sage-200/60">
      <MoonPhaseIcon phase={day.phase} className="h-9 w-9 shrink-0 text-sage-700" />
      <div className="min-w-0 flex-1">
        <p className="font-serif text-sage-800">{longDate(day.date, lang)}</p>
        <p className="text-xs text-sage-500">
          {t(`lunar.phase.${day.phase}` as never)} · {signLabel} · {t('lunar.lunar_day_short', { n: day.lunarDay })}
        </p>
      </div>
    </li>
  )
}

function longDate(iso: string, lang: 'ru' | 'en'): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(y!, m! - 1, d!))
  return new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  }).format(date)
}
